import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Send, Image, Video, Music2, Smile, X, Paperclip, Camera, TimerReset, ShieldOff, Sparkles, Wand2, Loader2, Globe } from 'lucide-react';
import { MediaPreview } from '../ui/media-preview';
import { EmojiPicker } from '../ui/emoji-picker';
import { GifPicker } from '../ui/gif-picker';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '../ui/use-toast';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { generateSmartChatReplies, rewriteChatMessage } from '@/services/aiAssistantService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';

interface MessageInputProps {
  onSendMessage: (content: string, mediaUrl?: string, mediaType?: string) => Promise<void> | void;
  replyingTo?: any;
  suggestedReplies?: string[];
  conversationKey?: string;
  outgoingMessagesCount?: number;
  localOnlyMode?: boolean;
  ephemeralIntervalLabel?: string;
  onToggleLocalOnlyMode?: () => void;
  onCycleEphemeralInterval?: () => void;
  onCancelReply?: () => void;
  onTypingChange?: (isTyping: boolean) => void;
}

const QUICK_REPLY_MEMORY_KEY = 'messages-quick-reply-memory-v1';
const QUICK_REPLY_SETTINGS_KEY = 'messages-quick-reply-settings-v1';
const QUICK_REPLY_SIGNAL_KEY = 'messages-quick-reply-signals-v1';

type SmartReplyTone = 'auto' | 'friendly' | 'professional' | 'brief';
type ReplyFreshness = 'new' | 'fresh' | 'warm' | 'stale';
type QuickReplySignal = { count: number; lastUsedAt: number };

const applyToneToReply = (reply: string, tone: SmartReplyTone) => {
  const normalized = reply.trim();
  if (!normalized) return normalized;

  if (tone === 'auto') {
    return normalized;
  }

  if (tone === 'friendly') {
    if (/[!?]$/.test(normalized)) return normalized;
    return `${normalized}!`;
  }

  if (tone === 'professional') {
    const withoutSlang = normalized
      .replace(/\b(thx|thanks!)\b/gi, 'Thank you')
      .replace(/\bgonna\b/gi, 'going to')
      .replace(/\bwanna\b/gi, 'want to');
    if (/[.!?]$/.test(withoutSlang)) return withoutSlang;
    return `${withoutSlang}.`;
  }

  const brief = normalized
    .split(/[,;]|\sand\s/i)[0]
    .trim();
  return brief.length > 32 ? `${brief.slice(0, 32).trim()}...` : brief;
};

export function MessageInput({ onSendMessage, replyingTo, suggestedReplies = [], conversationKey, outgoingMessagesCount = 0, localOnlyMode = false, ephemeralIntervalLabel, onToggleLocalOnlyMode, onCycleEphemeralInterval, onCancelReply, onTypingChange }: MessageInputProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const isTypingRef = useRef(false);
  const [quickReplyUsage, setQuickReplyUsage] = useState<Record<string, Record<string, number>>>({});
  const [quickReplySettings, setQuickReplySettings] = useState<Record<string, { enabled: boolean; tone?: SmartReplyTone }>>({});
  const [quickReplySignals, setQuickReplySignals] = useState<Record<string, Record<string, QuickReplySignal>>>({});
  const { user } = useAuth();
  const { toast } = useToast();
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiLoadingAction, setAiLoadingAction] = useState<string | null>(null);

  const handleFetchAiReplies = async () => {
    try {
      setIsAiLoading(true);
      setAiLoadingAction('Generating smart replies with DeepSeek...');
      const targetText = replyingTo?.content || message || '';
      const replies = await generateSmartChatReplies(targetText);
      setAiSuggestions(replies);
    } catch (err: any) {
      toast({
        title: 'AI Smart Reply Error',
        description: err?.message || 'Failed to generate replies.',
        variant: 'destructive',
      });
    } finally {
      setIsAiLoading(false);
      setAiLoadingAction(null);
    }
  };

  const handleAiRewrite = async (tone: 'friendly' | 'professional' | 'concise', lang?: string) => {
    if (!message.trim()) {
      toast({
        title: 'Empty Message',
        description: 'Type a message draft first before rewriting.',
      });
      return;
    }
    try {
      setIsAiLoading(true);
      setAiLoadingAction('Rewriting with DeepSeek AI...');
      const rewritten = await rewriteChatMessage(message, tone, lang);
      setMessage(rewritten);
      focusMessageInput();
    } catch (err: any) {
      toast({
        title: 'AI Rewrite Error',
        description: err?.message || 'Failed to rewrite message.',
        variant: 'destructive',
      });
    } finally {
      setIsAiLoading(false);
      setAiLoadingAction(null);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(QUICK_REPLY_MEMORY_KEY) || '{}');
      if (parsed && typeof parsed === 'object') {
        setQuickReplyUsage(parsed as Record<string, Record<string, number>>);
      }
    } catch {
      setQuickReplyUsage({});
    }

    try {
      const parsedSettings = JSON.parse(window.localStorage.getItem(QUICK_REPLY_SETTINGS_KEY) || '{}');
      if (parsedSettings && typeof parsedSettings === 'object') {
        setQuickReplySettings(parsedSettings as Record<string, { enabled: boolean; tone?: SmartReplyTone }>);
      }
    } catch {
      setQuickReplySettings({});
    }

    try {
      const parsedSignals = JSON.parse(window.localStorage.getItem(QUICK_REPLY_SIGNAL_KEY) || '{}');
      if (parsedSignals && typeof parsedSignals === 'object') {
        setQuickReplySignals(parsedSignals as Record<string, Record<string, QuickReplySignal>>);
      }
    } catch {
      setQuickReplySignals({});
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(QUICK_REPLY_MEMORY_KEY, JSON.stringify(quickReplyUsage));
  }, [quickReplyUsage]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(QUICK_REPLY_SETTINGS_KEY, JSON.stringify(quickReplySettings));
  }, [quickReplySettings]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(QUICK_REPLY_SIGNAL_KEY, JSON.stringify(quickReplySignals));
  }, [quickReplySignals]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current !== null) {
        window.clearTimeout(typingTimeoutRef.current);
      }

      if (isTypingRef.current) {
        onTypingChange?.(false);
      }
    };
  }, [onTypingChange]);

  useEffect(() => {
    messageInputRef.current?.focus();
  }, []);

  const focusMessageInput = () => {
    window.requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const scheduleTypingStop = () => {
    if (typingTimeoutRef.current !== null) {
      window.clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = window.setTimeout(() => {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        onTypingChange?.(false);
      }
    }, 1400);
  };

  const emitTypingActivity = (nextValue: string) => {
    const hasText = nextValue.trim().length > 0;

    if (hasText && !isTypingRef.current) {
      isTypingRef.current = true;
      onTypingChange?.(true);
    }

    if (!hasText && isTypingRef.current) {
      isTypingRef.current = false;
      onTypingChange?.(false);
      if (typingTimeoutRef.current !== null) {
        window.clearTimeout(typingTimeoutRef.current);
      }
      return;
    }

    if (hasText) {
      scheduleTypingStop();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!message.trim() && !mediaFile && !mediaPreview) || !user || uploading) return;

    try {
      setUploading(true);
      let mediaUrl = mediaPreview;
      const finalMediaType = mediaType;
      
      if (mediaFile && !localOnlyMode) {
        const storageRef = ref(storage, `messages/${user.uid}/${Date.now()}_${mediaFile.name}`);
        await uploadBytes(storageRef, mediaFile);
        mediaUrl = await getDownloadURL(storageRef);
      }

      await onSendMessage(message.trim(), mediaUrl || undefined, finalMediaType || undefined);

      if (isTypingRef.current) {
        isTypingRef.current = false;
        onTypingChange?.(false);
      }
      if (typingTimeoutRef.current !== null) {
        window.clearTimeout(typingTimeoutRef.current);
      }
      
      setMessage('');
      setMediaFile(null);
      setMediaPreview(null);
      setMediaType(null);
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      focusMessageInput();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear previous media
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }

    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    
    if (file.type.startsWith('image/')) {
      setMediaType('image');
    } else if (file.type.startsWith('video/')) {
      setMediaType('video');
    } else if (file.type.startsWith('audio/')) {
      setMediaType('audio');
    }

    focusMessageInput();
  };

  const clearMedia = () => {
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleQuickReplySend = async (reply: string) => {
    if (!reply.trim() || !user || uploading) return;

    const tone = conversationKey
      ? quickReplySettings[conversationKey]?.tone || 'auto'
      : 'auto';
    const finalReply = applyToneToReply(reply, tone);

    try {
      setUploading(true);
      await onSendMessage(finalReply);

      if (isTypingRef.current) {
        isTypingRef.current = false;
        onTypingChange?.(false);
      }
      if (typingTimeoutRef.current !== null) {
        window.clearTimeout(typingTimeoutRef.current);
      }

      setMessage('');
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      focusMessageInput();

      if (conversationKey && !localOnlyMode) {
        const now = Date.now();

        setQuickReplyUsage((prev) => {
          const current = prev[conversationKey] || {};
          return {
            ...prev,
            [conversationKey]: {
              ...current,
              [finalReply]: (current[finalReply] || 0) + 1,
            },
          };
        });

        setQuickReplySignals((prev) => {
          const current = prev[conversationKey] || {};
          const previousSignal = current[finalReply] || { count: 0, lastUsedAt: now };
          return {
            ...prev,
            [conversationKey]: {
              ...current,
              [finalReply]: {
                count: previousSignal.count + 1,
                lastUsedAt: now,
              },
            },
          };
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send quick reply',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const rankedSuggestedReplies = useMemo(() => {
    if (!suggestedReplies.length) {
      return [] as string[];
    }

    const usage = conversationKey ? quickReplyUsage[conversationKey] || {} : {};
    const signals = conversationKey ? quickReplySignals[conversationKey] || {} : {};
    const now = Date.now();

    const scoreReply = (reply: string) => {
      const signal = signals[reply];
      if (!signal) {
        return usage[reply] || 0;
      }

      const ageDays = Math.max(0, (now - signal.lastUsedAt) / (1000 * 60 * 60 * 24));
      const recencyMultiplier = 0.65 + (0.35 * Math.exp(-ageDays / 7));
      return signal.count * recencyMultiplier;
    };

    return [...suggestedReplies]
      .sort((a, b) => {
        const diff = scoreReply(b) - scoreReply(a);
        if (diff !== 0) return diff;
        return a.localeCompare(b);
      })
      .slice(0, 4);
  }, [conversationKey, quickReplySignals, quickReplyUsage, suggestedReplies]);

  const conversationUsageStats = useMemo(() => {
    if (!conversationKey) {
      return { usedCount: 0, topReply: null as string | null };
    }

    const usage = quickReplyUsage[conversationKey] || {};
    const entries = Object.entries(usage).sort((a, b) => b[1] - a[1]);
    const usedCount = entries.reduce((sum, [, count]) => sum + count, 0);
    const topReply = entries[0]?.[0] || null;

    return { usedCount, topReply };
  }, [conversationKey, quickReplyUsage]);

  const smartRepliesEnabled = conversationKey
    ? quickReplySettings[conversationKey]?.enabled !== false
    : true;

  const smartReplyTone: SmartReplyTone = conversationKey
    ? quickReplySettings[conversationKey]?.tone || 'auto'
    : 'auto';

  const conversationSignalStats = useMemo(() => {
    if (!conversationKey) {
      return { freshness: 'new' as ReplyFreshness, confidence: 0 };
    }

    const signals = quickReplySignals[conversationKey] || {};
    const entries = Object.values(signals);
    if (!entries.length) {
      return { freshness: 'new' as ReplyFreshness, confidence: 0 };
    }

    const now = Date.now();
    const totalCount = entries.reduce((sum, signal) => sum + signal.count, 0);
    const weightedAgeDays = entries.reduce((sum, signal) => {
      const ageDays = Math.max(0, (now - signal.lastUsedAt) / (1000 * 60 * 60 * 24));
      return sum + (ageDays * signal.count);
    }, 0) / Math.max(1, totalCount);

    let freshness: ReplyFreshness = 'stale';
    if (weightedAgeDays <= 2) freshness = 'fresh';
    else if (weightedAgeDays <= 10) freshness = 'warm';

    const confidenceBase = Math.min(70, totalCount * 6);
    const diversityBoost = Math.min(20, entries.length * 4);
    const freshnessBoost = freshness === 'fresh' ? 10 : freshness === 'warm' ? 5 : 0;
    const confidence = Math.min(99, confidenceBase + diversityBoost + freshnessBoost);

    return { freshness, confidence };
  }, [conversationKey, quickReplySignals]);

  const adoptionRate = useMemo(() => {
    if (outgoingMessagesCount <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((conversationUsageStats.usedCount / outgoingMessagesCount) * 100));
  }, [conversationUsageStats.usedCount, outgoingMessagesCount]);

  const toggleSmartReplies = () => {
    if (!conversationKey) return;

    setQuickReplySettings((prev) => {
      const currentConfig = prev[conversationKey] || { enabled: true, tone: 'auto' as SmartReplyTone };
      const current = currentConfig.enabled;
      const nextEnabled = current === false;
      return {
        ...prev,
        [conversationKey]: {
          ...currentConfig,
          enabled: nextEnabled,
        },
      };
    });
  };

  const setSmartReplyTone = (tone: SmartReplyTone) => {
    if (!conversationKey) return;

    setQuickReplySettings((prev) => {
      const currentConfig = prev[conversationKey] || { enabled: true, tone: 'auto' as SmartReplyTone };
      return {
        ...prev,
        [conversationKey]: {
          ...currentConfig,
          tone,
        },
      };
    });
  };

  const resetSmartReplyLearning = () => {
    if (!conversationKey) return;

    setQuickReplyUsage((prev) => {
      const next = { ...prev };
      delete next[conversationKey];
      return next;
    });

    setQuickReplySignals((prev) => {
      const next = { ...prev };
      delete next[conversationKey];
      return next;
    });
  };

  const tonedSuggestedReplies = useMemo(() => {
    if (smartReplyTone === 'auto') {
      return rankedSuggestedReplies;
    }

    return rankedSuggestedReplies
      .map((reply) => applyToneToReply(reply, smartReplyTone))
      .filter((reply, index, array) => array.findIndex((value) => value.toLowerCase() === reply.toLowerCase()) === index);
  }, [rankedSuggestedReplies, smartReplyTone]);

  return (
    <div className="relative">
      {/* Reply Preview */}
      {replyingTo && (
        <div className="p-3 border-b border-gray-200/50 bg-blue-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-1 h-8 bg-blue-500 rounded"></div>
              <div>
                <p className="font-medium">Replying to {replyingTo.senderName}</p>
                <p className="truncate max-w-64">{replyingTo.content}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onCancelReply}
              className="h-6 w-6"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Media Preview */}
      {mediaPreview && mediaType && (
        <div className="p-4 border-b border-gray-200/50">
          <div className="relative inline-block max-w-full">
            <MediaPreview
              url={mediaPreview}
              type={mediaType}
              className="max-w-full max-h-[220px]"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-lg"
              onClick={clearMedia}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Smart Reply Suggestions */}
      {(suggestedReplies.length > 0 || conversationUsageStats.usedCount > 0) && !message.trim() && !mediaPreview && (
        <div className="px-4 pt-3 pb-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5">
              Smart replies: {smartRepliesEnabled ? 'on' : 'off'}
            </span>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5">
              Used {conversationUsageStats.usedCount}x in this chat
            </span>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5">
              Adoption {adoptionRate}%
            </span>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 capitalize">
              Freshness {conversationSignalStats.freshness}
            </span>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5">
              Confidence {conversationSignalStats.confidence}%
            </span>
            {conversationUsageStats.topReply ? (
              <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-2 py-0.5 max-w-[220px] truncate">
                Top: {conversationUsageStats.topReply}
              </span>
            ) : null}
            {conversationKey ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={toggleSmartReplies}
                  className="h-6 px-2 text-xs"
                >
                  {smartRepliesEnabled ? 'Disable' : 'Enable'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={resetSmartReplyLearning}
                  className="h-6 px-2 text-xs"
                  disabled={conversationUsageStats.usedCount === 0}
                >
                  Reset learning
                </Button>
              </>
            ) : null}
          </div>

          {conversationKey ? (
            <div className="flex flex-wrap items-center gap-1">
              {(['auto', 'friendly', 'professional', 'brief'] as SmartReplyTone[]).map((tone) => (
                <Button
                  key={`smart-reply-tone-${tone}`}
                  type="button"
                  variant={smartReplyTone === tone ? 'default' : 'outline'}
                  size="sm"
                  className="h-6 px-2 text-xs rounded-full"
                  onClick={() => setSmartReplyTone(tone)}
                >
                  {tone}
                </Button>
              ))}
            </div>
          ) : null}

          {tonedSuggestedReplies.length > 0 && smartRepliesEnabled ? (
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {tonedSuggestedReplies.map((reply) => (
                <Button
                  key={`quick-reply-${reply}`}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  onClick={() => {
                    void handleQuickReplySend(reply);
                  }}
                  className="whitespace-nowrap rounded-full border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  {reply}
                </Button>
              ))}
            </div>
          ) : null}

          {aiSuggestions.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-2 pt-1 animate-fade-in">
              <span className="text-[11px] font-bold text-blue-600 flex items-center gap-1 shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-blue-500" /> DeepSeek AI:
              </span>
              {aiSuggestions.map((reply, i) => (
                <Button
                  key={`ai-reply-${i}-${reply}`}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  onClick={() => {
                    setMessage(reply);
                    focusMessageInput();
                  }}
                  className="whitespace-nowrap rounded-full border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-800 hover:bg-blue-100 shadow-sm text-xs font-medium"
                >
                  {reply}
                </Button>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAiSuggestions([])}
                className="h-6 w-6 p-0 rounded-full text-slate-400 hover:text-slate-600"
                title="Dismiss AI suggestions"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Input Area */}
      <form onSubmit={handleSubmit} className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          {/* File Upload Inputs */}
          <input
            type="file"
            accept="image/*,video/*,audio/*"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={imageInputRef}
            onChange={handleFileSelect}
          />
          <input
            type="file"
            accept="video/*"
            className="hidden"
            ref={videoInputRef}
            onChange={handleFileSelect}
          />
          
          {/* Message Input */}
          <div className="relative w-full order-1 sm:order-2 sm:flex-1">
            <textarea
              ref={messageInputRef}
              value={message}
              onChange={(e) => {
                const nextValue = e.target.value;
                setMessage(nextValue);
                emitTypingActivity(nextValue);
              }}
              placeholder={t('messages.typeMessage')}
              className="min-h-[44px] max-h-32 w-full rounded-2xl sm:rounded-full border border-gray-200 bg-gray-50 focus:bg-white pl-4 pr-12 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={uploading}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              style={{ 
                height: 'auto',
                minHeight: '44px'
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 128) + 'px';
              }}
            />
            
            {/* Send Button */}
            <Button 
              type="submit" 
              disabled={uploading || (!message.trim() && !mediaFile && !mediaPreview)}
              className="absolute right-2 bottom-2 h-8 w-8 rounded-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 p-0"
              size="icon"
            >
              {uploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="order-2 sm:order-1 flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onToggleLocalOnlyMode?.()}
              disabled={uploading}
              className={`h-9 w-9 sm:h-10 sm:w-10 rounded-full flex-shrink-0 ${
                localOnlyMode
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'hover:bg-blue-50 text-gray-500 hover:text-blue-600'
              }`}
              title={localOnlyMode ? 'Disable disappearing local-only mode' : 'Enable disappearing local-only mode'}
            >
              {localOnlyMode ? <ShieldOff className="h-5 w-5" /> : <TimerReset className="h-5 w-5" />}
            </Button>

            {localOnlyMode ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onCycleEphemeralInterval?.()}
                className="h-9 sm:h-10 rounded-full px-3 text-xs border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 flex-shrink-0"
                title="Cycle disappearing timer"
              >
                {ephemeralIntervalLabel || '30s'}
              </Button>
            ) : null}

            {/* DeepSeek AI Magic Compose Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={uploading || isAiLoading}
                  className={cn(
                    "h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-blue-50 text-blue-600 flex-shrink-0 transition-all",
                    isAiLoading && "animate-pulse bg-blue-100"
                  )}
                  title="DeepSeek AI Magic Compose"
                >
                  {isAiLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  ) : (
                    <Sparkles className="h-5 w-5" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 p-1.5 shadow-xl border-blue-100">
                <DropdownMenuLabel className="text-xs font-bold text-blue-900 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600" /> DeepSeek V4 Flash
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleFetchAiReplies}
                  className="text-xs cursor-pointer flex items-center gap-2 font-medium text-blue-700"
                >
                  <Wand2 className="w-3.5 h-3.5 text-blue-500" />
                  <span>Generate Quick Replies</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] text-muted-foreground font-semibold">
                  Rewrite Draft Tone
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => handleAiRewrite('friendly')}
                  disabled={!message.trim()}
                  className="text-xs cursor-pointer"
                >
                  ✨ Friendly & Warm
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleAiRewrite('professional')}
                  disabled={!message.trim()}
                  className="text-xs cursor-pointer"
                >
                  💼 Articulate & Professional
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleAiRewrite('concise')}
                  disabled={!message.trim()}
                  className="text-xs cursor-pointer"
                >
                  ✂️ Short & Punchy
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="text-xs cursor-pointer flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-blue-500" />
                    <span>Translate Draft</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-40 text-xs">
                    <DropdownMenuItem onClick={() => handleAiRewrite('friendly', 'English')}>English</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAiRewrite('friendly', 'Croatian')}>Hrvatski</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAiRewrite('friendly', 'Spanish')}>Español</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAiRewrite('friendly', 'German')}>Deutsch</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAiRewrite('friendly', 'French')}>Français</DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex-shrink-0"
              title="Send Image"
            >
              <Image className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => videoInputRef.current?.click()}
              disabled={uploading}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex-shrink-0"
              title="Send Video"
            >
              <Video className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex-shrink-0"
              title="Send File"
            >
              <Paperclip className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowGifPicker(!showGifPicker)}
              disabled={uploading}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-blue-50 text-gray-500 hover:text-blue-600 text-xs font-semibold flex-shrink-0"
              title="Send GIF"
            >
              GIF
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={uploading}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex-shrink-0"
              title="Add Emoji"
            >
              <Smile className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div className="absolute bottom-full right-4 mb-2 z-50">
            <EmojiPicker
              isOpen={showEmojiPicker}
              onClose={() => setShowEmojiPicker(false)}
              onEmojiSelect={(emoji) => {
                setMessage(prev => prev + emoji);
                setShowEmojiPicker(false);
                focusMessageInput();
              }}
            />
          </div>
        )}

        {/* GIF Picker */}
        {showGifPicker && (
          <div className="absolute bottom-full right-4 mb-2 z-50">
            <GifPicker
              isOpen={showGifPicker}
              onClose={() => setShowGifPicker(false)}
              onSelect={(gifUrl) => {
                setMediaPreview(gifUrl);
                setMediaType('image');
                setShowGifPicker(false);
                focusMessageInput();
              }}
            />
          </div>
        )}
      </form>
    </div>
  );
}